/**
 * AssigneeSelect — 현장 담당자·관리자 선택 / 조회 조건 (2026-10-12 개선사항 D.0 '직원 선택 UI').
 * 공용 MultiSelect(searchable) 그대로: 입력칸 클릭으로 열림, ▾/▴ 토글, 바깥 클릭·Esc 닫힘,
 * 이름 검색·체크로 여러 명, 검색 결과 1건이면 Enter 로 추가(검색어 비움), ↑↓ 이동, 칩 × 로 해제.
 * 목록 우측 배지: 사장님(주황) · (지정 화면만) 반대 역할 지정자(회색). 본인은 '(나)' — 목록에서만 표시.
 * 문구는 추출기획서(20261012) 기준: placeholder '직원을 선택하세요', 빈 결과 '검색 결과가 없습니다'.
 * notiIds 를 넘기면 칩마다 알림 받기 토글(TS_SITE_ASSIGN.NOTI_RECEIVE_YN, MultiSelect toggled — 현장상세 편집 20261015 E-0).
 * 이때 '(나)' 는 표시하지 않는다.
 */
import { Badge, MultiSelect } from '@indipia/ui-core';
import type { ComboboxOption } from '@indipia/ui-core';
import type { CompanyMember } from './types';

export interface AssigneeSelectProps {
  members: CompanyMember[];
  values: number[];
  onChange: (values: number[]) => void;
  /** 로그인 사용자 — 목록에 '(나)' 표시 */
  myId?: number | null;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** 지정 화면 — 반대 역할로 이미 지정된 사람(담당자 목록이면 관리자 지정자) */
  oppositeIds?: number[];
  /** 반대 역할 배지 문구('관리자' / '담당자') */
  oppositeLabel?: string;
  /** 구성원 목록에서 빠진 사람(해제·탈퇴)이 이미 선택돼 있을 때 칩에 보일 이름 */
  extraNames?: Record<number, string | null | undefined>;
  /** 본인을 목록 맨 위에 둔다(현장 등록 팝업 '현장 담당 지정') */
  meFirst?: boolean;
  /** 알림 받기를 켠 사람(values 의 부분집합). 새로 고른 사람은 꺼진 채로 들어온다 */
  notiIds?: number[];
  onNotiChange?: (ids: number[]) => void;
}

/** 선택 후보 — 구성원 + (구성원 목록에 없지만 이미 선택된 사람) */
function buildOptions(
  members: CompanyMember[], values: number[], myId: number | null | undefined,
  meFirst: boolean, extraNames?: Record<number, string | null | undefined>,
): ComboboxOption[] {
  const known = new Set(members.map((m) => m.memberId));
  const ordered = meFirst && myId != null
    ? [...members.filter((m) => m.memberId === myId), ...members.filter((m) => m.memberId !== myId)]
    : members;
  const options = ordered.map((m) => ({ value: String(m.memberId), label: m.memberName }));
  // 선택돼 있지만 구성원 목록에 없는 사람도 칩 이름은 보이게(옵션에 없으면 값 그대로 보인다)
  for (const v of values) {
    if (!known.has(v)) options.push({ value: String(v), label: extraNames?.[v] ?? `회원 ${v}` });
  }
  return options;
}

/** 목록 항목 — 이름(+ 본인 '(나)') · 오른쪽 배지(사장님 · 반대 역할 지정자) */
function optionRenderer(
  members: CompanyMember[], showMe: boolean, myId: number | null | undefined,
  oppositeIds?: number[], oppositeLabel?: string,
) {
  const byId = new Map(members.map((m) => [String(m.memberId), m]));
  return (opt: ComboboxOption) => {
    const m = byId.get(opt.value);
    const id = Number(opt.value);
    return (
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate">{opt.label}{showMe && myId != null && id === myId ? ' (나)' : ''}</span>
        <span className="ml-auto flex shrink-0 gap-1">
          {m?.roleCode === 'OWNER' && <Badge status="warning">사장님</Badge>}
          {oppositeLabel && oppositeIds?.includes(id) && <Badge status="mute">{oppositeLabel}</Badge>}
        </span>
      </span>
    );
  };
}

export function AssigneeSelect({
  members, values, onChange, myId, label, placeholder = '직원을 선택하세요', error, disabled, className, id,
  oppositeIds, oppositeLabel, extraNames, meFirst = false, notiIds, onNotiChange,
}: AssigneeSelectProps) {
  return (
    <MultiSelect
      id={id}
      label={label}
      className={className}
      values={values.map(String)}
      onChange={(next) => onChange(next.map(Number))}
      options={buildOptions(members, values, myId, meFirst, extraNames)}
      searchable
      caretToggle
      enterSelectsSingle
      placeholder={placeholder}
      emptyText="검색 결과가 없습니다"
      error={error}
      disabled={disabled}
      renderOption={optionRenderer(members, !notiIds, myId, oppositeIds, oppositeLabel)}
      toggled={notiIds?.map(String)}
      onToggledChange={(next) => onNotiChange?.(next.map(Number))}
    />
  );
}
